import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Form, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormMessage,
  FormDescription 
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Monitor } from 'lucide-react';
import { Screen } from '@/models/screen';
import { 
  MAINTENANCE_PROBLEMS, 
  MaintenanceProblemType,
  ESTIMATED_TIME_OPTIONS
} from '@/models/maintenance';
import { createMaintenanceReport } from '@/services/maintenanceService';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import MaintenanceNotificationToast from './MaintenanceNotificationToast';

// Definir el esquema de validación
const formSchema = z.object({
  problems: z.array(z.string()).min(1, 'Debe seleccionar al menos un problema'),
  estimatedTimeHours: z.number().min(1, 'Debe seleccionar un tiempo estimado'),
  details: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface MaintenanceReportFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screen: Screen;
  onReportCreated: () => void;
}

const MaintenanceReportForm: React.FC<MaintenanceReportFormProps> = ({
  open,
  onOpenChange,
  screen,
  onReportCreated
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { refreshNotifications } = useNotifications();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      problems: [],
      estimatedTimeHours: 1,
      details: '',
    },
  });

  const handleSubmit = async (data: FormValues) => {
    if (!user) {
      toast.error('Debe iniciar sesión para reportar un mantenimiento');
      return;
    }

    setIsSubmitting(true);
    try {
      // Crear el objeto con los datos del reporte
      const reportData = {
        screenId: screen.id,
        problems: data.problems as MaintenanceProblemType[],
        estimatedTimeHours: data.estimatedTimeHours,
        details: data.details || '',
        reportedById: user.id,
      };

      // Obtener información adicional para la base de datos
      const screenName = screen.name || 'Pantalla sin nombre';
      const screenLocation = screen.location || 'Ubicación desconocida';
      const reportedByName = user.profile?.full_name || user.email || 'Usuario desconocido';

      // Llamar a la función asíncrona con await
      const result = await createMaintenanceReport(
        reportData, 
        screenName, 
        screenLocation, 
        reportedByName
      );
      
      if (result) {
        // Mostrar notificación personalizada
        toast.custom((t) => (
          <MaintenanceNotificationToast 
            notification={{
              id: result.id,
              screenId: result.screenId,
              screenName: screenName,
              screenLocation: screenLocation,
              problems: result.problems,
              details: result.details,
              createdAt: result.createdAt,
              estimatedTimeHours: result.estimatedTimeHours,
              read: false,
              completed: false
            }} 
          />
        ), {
          duration: 6000,
          position: 'top-center'
        });
        
        // Actualizar notificaciones para otros usuarios
        refreshNotifications();
        
        // Resto del código
        onReportCreated();
        onOpenChange(false);
        form.reset();
      } else {
        toast.error('Error al registrar el mantenimiento');
      }
    } catch (error) {
      console.error('Error al crear reporte de mantenimiento:', error);
      toast.error('Error al registrar el mantenimiento');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 mb-1">
            <Monitor className="h-5 w-5" /> Esta pantalla presenta el siguiente problema:
          </DialogTitle>
          <div className="text-sm font-medium text-gray-700">
            PANTALLA {screen?.name} - {screen?.location}
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div>
                <p className="font-medium mb-2">Selecciona los problemas (puedes marcar varios):</p>
                <div className="space-y-2">
                  {MAINTENANCE_PROBLEMS.map((problem) => (
                    <FormField
                      key={problem.id}
                      control={form.control}
                      name="problems"
                      render={({ field }) => {
                        return (
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={problem.id}
                              checked={field.value?.includes(problem.type)}
                              onCheckedChange={(checked) => {
                                const currentValues = [...field.value || []];
                                if (checked) {
                                  field.onChange([...currentValues, problem.type]);
                                } else {
                                  field.onChange(
                                    currentValues.filter((value) => value !== problem.type)
                                  );
                                }
                              }}
                            />
                            <label 
                              htmlFor={problem.id} 
                              className="text-sm font-normal leading-none cursor-pointer"
                            >
                              {problem.icon} {problem.label}
                            </label>
                          </div>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </div>

              <div>
                <div className="flex items-center mb-1">
                  <span className="mr-2">🕒</span>
                  <p className="font-medium">Tiempo estimado de resolución:</p>
                </div>
                <FormField
                  control={form.control}
                  name="estimatedTimeHours"
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      defaultValue={field.value.toString()}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Seleccione el tiempo estimado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ESTIMATED_TIME_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value.toString()}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div>
                <div className="flex items-center mb-1">
                  <span className="mr-2">👉</span>
                  <p className="font-medium">Describa el problema con más detalle:</p>
                </div>
                <FormField
                  control={form.control}
                  name="details"
                  render={({ field }) => (
                    <Textarea
                      placeholder="Detalles adicionales..."
                      className="resize-none"
                      {...field}
                    />
                  )}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-amber-400 hover:bg-amber-500 text-black"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrar Mantenimiento
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceReportForm; 